import { FC, useEffect, useMemo } from "react";
import { Box, Button, Typography } from "@mui/material";
import { InfoOutlined, DoneOutlined } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { generateRoute, makeSxStyles } from "ui";
import DashboardContentLayout from "../../components/dashboard-content-layout";
import Navbar from "@/components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "@/shared/components/header-items/HeaderItems";
import MapComponent from "@/components/map/map";
import MapProvider from "@/components/map/map-provider";
import { MapComponentProps, MapMarker } from "@/components/map/map.types";
import { AdRoutes } from "@/constants/ad-routes";
import useCreateTempRouteStore from "@/state/create-temp-route-store";
import CreateTempRouteWorkflowStepper from "./create-temp-route-workflow-stepper";
import CreateTempRouteSidebarSequence from "./create-temp-route-sidebar-sequence";
import CreateTempRouteWarningBanner from "./create-temp-route-warning-banner";

const CreateTempRouteSequencePage: FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { date } = useParams<{ date: string }>();
  const {
    date: storedDate,
    candidates,
    mapZoom,
    mapCentering,
    lastSequence,
    query,
    setAddressSequence,
    setMapZoom,
    setMapCentering,
    setQuery,
  } = useCreateTempRouteStore();

  // Filters the fixed candidate set client-side - no backend location search
  // needed since candidates are just whatever the user already picked.
  const searchedAddresses = useMemo(() => {
    if (!query.trim()) return [];
    const needle = query.trim().toLowerCase();
    return candidates.filter((candidate) =>
      candidate.address?.toLowerCase().includes(needle),
    );
  }, [candidates, query]);

  // AddressBarControl clears the query itself after selection, so this only
  // needs to handle panning the map to the picked address.
  const handleSearchResultSelect = (marker: MapMarker) => {
    setMapCentering([marker]);
  };

  const remainingSequences = candidates.filter(
    (address) =>
      address.locationSequence === null ||
      address.locationSequence === undefined,
  ).length;
  const allSequenced = candidates.length > 0 && remainingSequences === 0;

  // Candidates only live in this in-memory store - if missing (direct nav,
  // refresh), bounce back to the route planner.
  useEffect(() => {
    if (!storedDate || candidates.length === 0) {
      navigate(generateRoute(AdRoutes.routePlanner, { date: date || "" }), {
        replace: true,
      });
    }
  }, [storedDate, candidates.length, navigate, date]);

  const handleSaveSequence = () => {
    if (!date) return;
    navigate(generateRoute(AdRoutes.createTempRouteDetails, { date }));
  };

  const mapProps: MapComponentProps = {
    mapWidth: "100%",
    mapHeight: "708px",
    markers: candidates,
    mapZoom,
    mapCentering,
    showTopMapControl: false,
    lastSequence,
    selectedAddresses: candidates,
    isLoading: false,
    isSearchLoading: false,
    sequencing: true,
    query,
    searchedAddresses,
    setQuery,
    onSearchResultSelect: handleSearchResultSelect,
    setAddressSequence,
    onMapZoomChange: setMapZoom,
    onDrawingToggle: () => undefined,
    setMapCentering: (_id, addresses) => setMapCentering(addresses),
  };

  return (
    <DashboardContentLayout>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              {
                label: "Route Manager",
                url: date
                  ? generateRoute(AdRoutes.routePlanner, { date })
                  : undefined,
              },
              {
                label: "Create Temporary Route",
              },
            ]}
          />
          <HeaderItemsRight>
            <Button
              disabled={!allSequenced}
              variant="contained"
              onClick={handleSaveSequence}
              data-testid="save-sequence-button"
            >
              Save Sequence
            </Button>
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>

      <CreateTempRouteWorkflowStepper />

      <Box display="flex">
        <Box sx={styles.mapWrapper}>
          <MapProvider>
            <MapComponent {...mapProps} />
          </MapProvider>
        </Box>

        <CreateTempRouteSidebarSequence
          customHeader={
            <Box sx={styles.sideBarTitleInfo}>
              <CreateTempRouteWarningBanner />
              <Typography sx={styles.instructions}>
                Sequence routes by clicking on each address on the map, starting
                with the first and ending with the last.
              </Typography>
              <Box sx={styles.infoBar}>
                {!allSequenced ? (
                  <>
                    <InfoOutlined
                      style={{ marginRight: "8px", color: "#8ACEFF" }}
                    />
                    <Typography>
                      {remainingSequences === 0
                        ? candidates.length
                        : remainingSequences}{" "}
                      addresses to be sequenced.
                    </Typography>
                  </>
                ) : (
                  <>
                    <DoneOutlined
                      style={{
                        marginRight: "8px",
                        color: "rgba(63, 191, 158, 1)",
                      }}
                    />
                    <Typography sx={{ fontSize: "13px" }}>
                      Sequence is ready to be confirmed.
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          }
        />
      </Box>
    </DashboardContentLayout>
  );
};

const useStyles = makeSxStyles(() => ({
  mapWrapper: {
    width: "calc(100% - 324px)",
  },
  sideBarTitleInfo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "16px",
    paddingBottom: "15px",
    borderBottom: "1px solid rgba(65, 71, 77, 1)",
  },
  instructions: {
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "20px",
    color: "#C1C7CE",
  },
  infoBar: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
}));

export default CreateTempRouteSequencePage;
